import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { 
  X, 
  Sparkles, 
  TrendingUp, 
  Calendar, 
  Target,
  Loader2,
  Check,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";

// Trigger-specific content configurations
const TRIGGER_CONTENT = {
  shortfall_alert: {
    icon: Shield,
    iconColor: "text-red-400",
    iconBg: "bg-red-500/20",
    title: "Avoid this shortfall",
    body: "We'll show you exactly how to adjust your plan and stay on track.",
    benefits: [
      "See exactly which bills to adjust",
      "Get personalized recommendations",
      "Prevent future shortfalls automatically"
    ]
  },
  simulation: {
    icon: Target,
    iconColor: "text-purple-400",
    iconBg: "bg-purple-500/20",
    title: "See how to become debt-free faster",
    body: "Unlock your full debt payoff analysis with exact timelines and savings.",
    benefits: [
      "See exactly how many months you'll save",
      "Calculate total interest savings",
      "Get your personalized payoff roadmap"
    ]
  },
  forecast: {
    icon: Calendar,
    iconColor: "text-blue-400",
    iconBg: "bg-blue-500/20",
    title: "See your full financial future",
    body: "Plan confidently with a complete 6-month view of your finances.",
    benefits: [
      "See 6 months ahead, not just 1",
      "Spot problems before they happen",
      "Make smarter financial decisions"
    ]
  },
  safe_to_spend: {
    icon: TrendingUp,
    iconColor: "text-emerald-400",
    iconBg: "bg-emerald-500/20",
    title: "Increase your safe-to-spend",
    body: "We'll show you exactly how to optimise your plan and give yourself more money each week.",
    benefits: [
      "Find hidden savings in your bills",
      "Optimize your payment timing",
      "Maximize your weekly spending power"
    ]
  },
  generic: {
    icon: Sparkles,
    iconColor: "text-amber-400",
    iconBg: "bg-amber-500/20",
    title: "Take control of your money",
    body: "Stop guessing. Start knowing exactly what to do.",
    benefits: [
      "See your finances 6 months ahead",
      "Get exact steps to avoid shortfalls",
      "Pay off debt faster"
    ]
  }
};

export default function UpgradeModal({ 
  isOpen, 
  onClose, 
  triggerSource = "generic",
  contextData = {} // Optional context like shortfall amount
}) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const content = TRIGGER_CONTENT[triggerSource] || TRIGGER_CONTENT.generic;
  const Icon = content.icon;

  const trackPaywallEvent = async (eventName) => {
    try {
      await axios.post(`${API}/track/event`, {
        event_name: eventName,
        metadata: {
          trigger_source: triggerSource,
          ...contextData
        }
      }, { withCredentials: true });
    } catch (error) {
      console.error("Tracking error:", error);
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    await trackPaywallEvent("paywall_clicked");
    
    try {
      const response = await axios.post(`${API}/subscription/start-trial`, {}, {
        withCredentials: true
      });
      
      await trackPaywallEvent("trial_started");
      toast.success(`🎉 Trial started! You have ${response.data.trial_days} days of premium access.`);
      updateUser({ is_premium: true, plan_type: "trial" });
      onClose();
    } catch (error) {
      if (error.response?.status === 400) {
        // User already had trial, redirect to payment
        toast.info("You've already used your trial. Let's get you subscribed!");
        onClose();
        navigate("/settings");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Track that paywall was shown
  useEffect(() => {
    if (isOpen) {
      trackPaywallEvent("paywall_shown");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="upgrade-modal"
    >
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="relative p-6 pb-4 text-center">
          <div className={`w-16 h-16 rounded-2xl ${content.iconBg} flex items-center justify-center mx-auto mb-4`}>
            <Icon className={`w-8 h-8 ${content.iconColor}`} />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">
            {content.title}
          </h2>
          
          <p className="text-slate-300">
            {content.body}
          </p>
        </div>

        {/* Benefits */}
        <div className="px-6 pb-4">
          <ul className="space-y-3">
            {content.benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-emerald-400" />
                </div>
                <span className="text-sm text-slate-200">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="p-6 pt-2 space-y-3">
          <Button
            onClick={handleStartTrial}
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold text-base rounded-xl"
            data-testid="start-trial-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Starting trial...
              </>
            ) : (
              "Start 14-day free trial"
            )}
          </Button>
          
          <p className="text-center text-xs text-slate-500">
            Card required • Cancel anytime before trial ends
          </p>
        </div>
      </div>
    </div>
  );
}

// Export trigger source constants for consistency
export const TRIGGER_SOURCES = {
  SHORTFALL: "shortfall_alert",
  SIMULATION: "simulation",
  FORECAST: "forecast",
  SAFE_TO_SPEND: "safe_to_spend",
  GENERIC: "generic"
};
